import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout, Header, SpaceBetween, Grid, Button, } from '@amzn/awsui-components-react-v3/polaris';
import AddSeatsAdditionsContainer from '../OrderAdditionsDetailsContainer/AddSeatsAdditionsDetailContainer';
import ModifySubscriptionDetailsContainer from '../SubscriptionDetailsFormContainer/ModifySubscriptionDetailsContainer';
import ModifySubscriptionBillingContainer from '../BillingContainer/ModifySubscriptionBillingContainer';
import AddSeatsSummaryContainer from '../SubscriptionSummaryContainer/AddSeatsSummaryContainer';
import SBTSBreadcrumbGroup from '../BreadcrumbGroup/BreadcrumbGroup';
import { getRegionFromCountryName, SessionKey, MIN_NUMBER_OF_MIDTERM_SEATS, MAX_NUMBER_OF_MIDTERM_SEATS, OPPORTUNITY_REGEX } from '../../common/constants';
import { getSubscriptionRequestBySubscriptionId, useGetOfferPriceForAccount } from '../../user/api/queries';
import { useAddSeatsMidtermMutation } from '../../user/api/mutations';
import { ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT } from '../../common/routes';
import { UserContext } from '../../App';
import LoadingContent from '../LoadingContent/LoadingContent';
import { useTranslation } from 'react-i18next';
import { triggerAnalyticsEvent } from '../../analytics/createEventTrigger';
import { getNodeEnvironment } from '../../common/nodeEnvironment';
export const AddSeatsPage = (props) => {
    const { t } = useTranslation();
    const parentSubscriptionRequestId = sessionStorage.getItem(SessionKey.PARENT_SUBSCRIPTION_REQUEST_ID);
    const [parentOrderDetails, setParentOrderDetails] = useState(null);
    const [discountPercentage, setDiscountPercentage] = useState();
    const navigate = useNavigate();
    const user = useContext(UserContext);
    const defaultFieldData = {
        value: '',
        error: ''
    };
    const [formData, setFormData] = useState({
        numberOfSeats: defaultFieldData,
        aceOpportunityId: defaultFieldData
    });
    // Fetch subscription details using the query
    const { data: subscriptionData, loading: subscriptionLoading, error: subscriptionError } = getSubscriptionRequestBySubscriptionId(parentSubscriptionRequestId);
    const { addSeatsMidterm, addSeatsMidtermLoading, addSeatsMidtermError } = useAddSeatsMidtermMutation();
    const { data: offerPriceData, loading: offerPriceDataLoading, error: offerPriceDataError } = useGetOfferPriceForAccount(sessionStorage.getItem(SessionKey.AWS_ACCOUNT_ID), user?.id, user?.email, (sessionStorage.getItem(SessionKey.IS_AMAZON_TRAINING_PARTNER) ? true : false), user?.vibeId);
    const linkedAwsAccountId = sessionStorage.getItem(SessionKey.AWS_ACCOUNT_ID) || "";
    const payerAwsAccountid = offerPriceData?.getPayerAccountOfferPrice?.payerAccountId;
    // Process subscription data when available
    useEffect(() => {
        if (subscriptionData?.subscriptionRequestById) {
            const subscription = subscriptionData.subscriptionRequestById;
            setParentOrderDetails({
                orderId: subscription.orderId || 'NA',
                originalSeats: subscription.numberOfSeats,
                subscriptionEndDate: subscription.subscriptionEndDate,
                pricePerSeat: subscription.pricePerSeat,
                accountId: subscription.linkedAccountId,
                discountPercentage: subscription.discountPercentage || 0,
                partnerRequest: subscription.partnerRequest
            });
        }
        else if (!subscriptionLoading && !subscriptionError && parentSubscriptionRequestId) {
            navigate(ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT, {
                state: { notification: { text: t('subscription_form_something_went_wrong'), type: 'error' } }
            });
        }
    }, [subscriptionData]);
    // Validation functions for each field
    const validateNumberOfSeats = (value) => {
        if (!value) {
            return t("subscription_form_number_of_seats_required_error_text");
        }
        if (value && Number.isNaN(parseInt(value))) {
            return t('subscription_form_number_of_seats_should_be_number_error_text');
        }
        if (value && !Number.isInteger(parseFloat(value))) {
            return t('subscription_form_number_of_seats_should_be_integer_error_text');
        }
        if (value && +value < MIN_NUMBER_OF_MIDTERM_SEATS) {
            return t('subscription_form_minimum_number_of_seats_error_text', { minSeats: MIN_NUMBER_OF_MIDTERM_SEATS });
        }
        if (value && +value > (MAX_NUMBER_OF_MIDTERM_SEATS - parseInt(sessionStorage.getItem(SessionKey.TOTAL_EXISTING_SEATS) || '0'))) {
            return t('subscription_form_number_of_seats_should_be_at_less_than_10000_error_text');
        }
        return '';
    };
    const validateAceOpportunityId = (value) => {
        if (parentOrderDetails?.partnerRequest && !value) {
            return t("subscription_form_ace_opportunity_id_required_error_text");
        }
        if (value && !OPPORTUNITY_REGEX.test(value)) {
            return t("subscription_form_ace_opportunity_id_invalid_error_text");
        }
        return '';
    };
    // Form data change handlers
    const handleFormDataChange = {
        numberOfSeats: (e) => {
            const value = e.detail.value;
            const error = validateNumberOfSeats(value);
            setFormData(prev => ({
                ...prev,
                numberOfSeats: {
                    value,
                    error
                }
            }));
            return !error;
        },
        aceOpportunityId: (e) => {
            const value = e.detail.value;
            const error = validateAceOpportunityId(value);
            setFormData(prev => ({
                ...prev,
                aceOpportunityId: {
                    value,
                    error
                }
            }));
            return !error;
        }
    };
    // Form validation
    const validateForm = () => {
        const numberOfSeatsValid = handleFormDataChange.numberOfSeats({
            detail: { value: formData.numberOfSeats.value }
        });
        const aceOpportunityIdValid = handleFormDataChange.aceOpportunityId({
            detail: { value: formData.aceOpportunityId.value }
        });
        return numberOfSeatsValid && aceOpportunityIdValid;
    };
    // Prepare data for addSeatsMidterm
    const prepareAddSeatsMidtermData = () => {
        if (!parentOrderDetails || !subscriptionData?.subscriptionRequestById)
            return null;
        const subscription = subscriptionData.subscriptionRequestById;
        const seats = parseInt(formData.numberOfSeats.value);
        return {
            id: parentSubscriptionRequestId,
            numberOfSeats: seats,
            awsToken: sessionStorage.getItem(SessionKey.AWS_ACCOUNT_JWT),
            linkedAccountId: linkedAwsAccountId,
            payerAccountId: payerAwsAccountid,
            geo: parentOrderDetails.partnerRequest ?
                getRegionFromCountryName(subscription.endCustomerCountry) :
                getRegionFromCountryName(subscription.customerCountry),
            discountPercentage: discountPercentage ? +discountPercentage : parentOrderDetails.discountPercentage,
            // Optional fields for partner request
            ...(subscription.partnerRequest && {
                aceOpportunityId: formData.aceOpportunityId.value.trim()
            }),
        };
    };
    // Handle form submission
    const handleAddSeatsSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            const addSeatsMidtermData = prepareAddSeatsMidtermData();
            if (!addSeatsMidtermData)
                return;
            addSeatsMidterm({
                onCompleted: () => {
                    // Clear session storage
                    sessionStorage.removeItem(SessionKey.AWS_ACCOUNT_ID);
                    sessionStorage.removeItem(SessionKey.AWS_ACCOUNT_JWT);
                    sessionStorage.removeItem(SessionKey.PARENT_SUBSCRIPTION_REQUEST_ID);
                    sessionStorage.removeItem(SessionKey.TOTAL_EXISTING_SEATS);
                    // Show success message and navigate back
                    navigate(ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT, {
                        state: { notification: { text: t('Add_seats_success_message'), type: 'success' } }
                    });
                },
                onError: (error) => {
                    console.error(error);
                    props.setNotification({ text: getFinalizeTeamSubscriptionErrorMessage(error.message), type: 'error' });
                },
                variables: { input: addSeatsMidtermData }
            });
        }
        else {
            console.error(`Form validation failed`);
        }
    };
    /**
     * Return custom error messages for cases: Max subscription reached, Inactive AWS Account
     * otherwise return a generic message
     * @param errorMessageFromServer
     * @returns
     */
    const getFinalizeTeamSubscriptionErrorMessage = (errorMessageFromServer) => {
        const MAX_SEATS_REACHED = 'Total seats across parent and child requests would exceed the limit (10000)'.toLowerCase();
        const MAX_SUBSCRIPTION_REACHED = 'Maximum number of child requests'.toLowerCase();
        const AWS_ACCOUNT_NOT_ACTIVE = 'has invalid state'.toLowerCase();
        if (errorMessageFromServer.toLowerCase().includes(MAX_SUBSCRIPTION_REACHED)) {
            triggerAnalyticsEvent('toolComplete', `SBTS_${(getNodeEnvironment())} | add_seats | form_submission | event_failed_of_max_subscriptions`);
            return t('subscription_form_max_subscription_limit_reached');
        }
        else if (errorMessageFromServer.toLowerCase().includes(AWS_ACCOUNT_NOT_ACTIVE)) {
            triggerAnalyticsEvent('toolComplete', `SBTS_${(getNodeEnvironment())} | add_seats | form_submission | event_failed_of_inactive_aws_account`);
            return t('subscription_form_account_not_active', { awsAccountId: sessionStorage.getItem(SessionKey.AWS_ACCOUNT_ID) });
        }
        else if (errorMessageFromServer.toLowerCase().includes(MAX_SEATS_REACHED)) {
            triggerAnalyticsEvent('toolComplete', `SBTS_${(getNodeEnvironment())} | add_seats | form_submission | event_failed_of_max_seats`);
            return t('subscription_form_number_of_seats_should_be_at_less_than_10000_error_text');
        }
        else {
            triggerAnalyticsEvent('toolComplete', `SBTS_${(getNodeEnvironment())} | add_seats | form_submission | event_failed`);
            return t('subscription_form_something_went_wrong');
        }
    };
    // Cancel handler
    const handleCancel = () => {
        navigate(ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT);
    };
    if (subscriptionLoading || !parentOrderDetails) {
        return _jsx(LoadingContent, { text: "Loading" });
    }
    return (_jsx(AppLayout, { content: _jsx("form", { onSubmit: handleAddSeatsSubmit, children: _jsxs(SpaceBetween, { size: "l", children: [_jsx(SBTSBreadcrumbGroup, { items: [
                            { text: t('breadcrumb_subscription_management_page'), href: "/" },
                            { text: t('Add_seats_option'), href: "#" }
                        ] }), _jsx(Header, { variant: "h1", actions: _jsxs(SpaceBetween, { direction: "horizontal", size: "xs", children: [_jsx(Button, { onClick: handleCancel, children: "Cancel" }), _jsx(Button, { variant: "primary", disabled: addSeatsMidtermLoading, loading: addSeatsMidtermLoading, onClick: handleAddSeatsSubmit, children: t("subscription_form_submit_button_text") })] }), children: t("Add_seats_page_header") }), _jsxs(Grid, { gridDefinition: [
                            { colspan: { default: 12, xxs: 7 } },
                            { colspan: { default: 12, xxs: 5 } },
                        ], children: [_jsxs(SpaceBetween, { size: "xl", children: [_jsx(ModifySubscriptionDetailsContainer, { orderDetails: parentOrderDetails }), _jsx(AddSeatsAdditionsContainer, { formData: formData, onFormDataChange: handleFormDataChange, isPartnerAccount: parentOrderDetails.partnerRequest }), _jsx(ModifySubscriptionBillingContainer, { accountId: payerAwsAccountid })] }), _jsx(AddSeatsSummaryContainer, { orderDetails: parentOrderDetails, formData: formData, setDiscount: setDiscountPercentage, offerPriceData: offerPriceData })] })] }) }), navigationHide: true, toolsHide: true }));
};
export default AddSeatsPage;
